/**
 * ProductStatusToggle Component - Story 6.6: Create Product Status Toggle
 * Quick toggle switch for product availability in My Products table
 *
 * Rules:
 * - qty < 20: Toggle forced OFF (inactive). Cannot activate. Alert shown on click.
 * - qty >= 20: Toggle works normally (ACTIVE <-> INACTIVE).
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { toggleProductStatus, ProductsApiError } from '../services/productsService';
import type { ProductStatus, ProductStatusToggleProps, ApiErrorResponse } from '../types/products.types';
import { ProductStatus as ProductStatusEnum } from '../constants/statusConstants';

// Products below 20 kg are auto-inactive and cannot be activated
const LOW_STOCK_THRESHOLD = 20;

export const ProductStatusToggle: React.FC<ProductStatusToggleProps & { availableQuantity?: number }> = ({
  productId,
  productName,
  currentStatus,
  availableQuantity = 0,
  onStatusChange,
  onError,
  isLoading: externalIsLoading = false,
  disabled = false,
  className,
}) => {
  const [optimisticStatus, setOptimisticStatus] = useState<ProductStatus>(currentStatus);
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLowStockAlertOpen, setIsLowStockAlertOpen] = useState(false);
  const [pendingNewStatus, setPendingNewStatus] = useState<ProductStatus | null>(null);

  // Sync optimistic status from parent
  useEffect(() => {
    setOptimisticStatus(currentStatus);
  }, [currentStatus]);

  // Stock checks
  const isInsufficientStock = availableQuantity < LOW_STOCK_THRESHOLD;
  const isZeroStock = availableQuantity === 0;
  const isLowStock = availableQuantity > 0 && availableQuantity < LOW_STOCK_THRESHOLD;

  // KEY FIX: When qty < 20, toggle is ALWAYS shown as OFF regardless of DB status
  const isChecked = isInsufficientStock ? false : optimisticStatus === ProductStatusEnum.ACTIVE;

  /**
   * Handle toggle click
   */
  const handleToggleChange = useCallback(
    (newChecked: boolean) => {
      const newStatus: ProductStatus = newChecked ? ProductStatusEnum.ACTIVE : ProductStatusEnum.INACTIVE;

      // If qty < 20 and user tries to activate -> show alert dialog
      if (newStatus === ProductStatusEnum.ACTIVE && isInsufficientStock) {
        setIsLowStockAlertOpen(true);
        return;
      }

      // Deactivating -> show confirmation dialog
      if (newStatus === ProductStatusEnum.INACTIVE) {
        setPendingNewStatus(newStatus);
        setIsConfirmDialogOpen(true);
      } else {
        // Activating with qty >= 20 -> proceed directly
        performStatusUpdate(newStatus);
      }
    },
    [isInsufficientStock]
  );

  /**
   * Perform the actual API status update
   */
  const performStatusUpdate = useCallback(
    async (newStatus: ProductStatus) => {
      const previousStatus = optimisticStatus;

      try {
        setIsLoading(true);
        setOptimisticStatus(newStatus);

        await toggleProductStatus(productId, newStatus);

        if (onStatusChange) {
          onStatusChange(productId, newStatus);
        }
      } catch (error) {
        console.error('[ProductStatusToggle] Status update failed:', error);
        setOptimisticStatus(previousStatus);

        if (onError) {
          onError({
            message:
              error instanceof ProductsApiError
                ? error.message
                : 'Failed to update product status. Please try again.',
            statusCode: error instanceof ProductsApiError ? error.statusCode : 500,
            errorCode: error instanceof ProductsApiError ? error.errorCode : 'UPDATE_ERROR',
          });
        }
      } finally {
        setIsLoading(false);
        setPendingNewStatus(null);
        setIsConfirmDialogOpen(false);
      }
    },
    [productId, optimisticStatus, onStatusChange, onError]
  );

  const handleConfirmDeactivation = useCallback(() => {
    if (pendingNewStatus) {
      performStatusUpdate(pendingNewStatus);
    }
  }, [pendingNewStatus, performStatusUpdate]);

  const handleCancelDeactivation = useCallback(() => {
    setPendingNewStatus(null);
    setIsConfirmDialogOpen(false);
  }, []);

  const isDisabled = disabled || isLoading || externalIsLoading;

  // Tooltip
  const toggleTooltip = isZeroStock
    ? 'Inactive: Out of stock (0 kg). Add stock (min 20 kg) to activate.'
    : isLowStock
      ? `Inactive: Low stock (${availableQuantity} kg). Minimum 20 kg required to activate.`
      : isChecked
        ? 'Click to deactivate product'
        : 'Click to activate product';

  return (
    <>
      <div className={cn('flex items-center gap-2', className)}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2">
                <Switch
                  checked={isChecked}
                  onCheckedChange={handleToggleChange}
                  disabled={isDisabled}
                  aria-label={`Toggle ${productName} status`}
                  data-testid={`toggle-status-${productId}`}
                />
                {/* Red warning icon for zero stock */}
                {isZeroStock && (
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                )}
                {/* Yellow warning icon for low stock (1-19 kg) */}
                {isLowStock && (
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>{toggleTooltip}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {isLoading && (
          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
        )}
      </div>

      {/* LOW STOCK ALERT - shown when user tries to activate with qty < 20 */}
      <AlertDialog open={isLowStockAlertOpen} onOpenChange={setIsLowStockAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Cannot Activate Product
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              <strong>{productName}</strong> cannot be activated because the available
              quantity is <strong className="text-red-600">{availableQuantity} kg</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="p-3 bg-yellow-50 border border-yellow-300 rounded-md">
            <p className="text-sm text-yellow-800 font-medium">
              The product status will automatically become Active when the available
              quantity becomes <strong>20 kg or more</strong>.
            </p>
          </div>
          <div className="flex justify-end pt-2">
            <AlertDialogAction onClick={() => setIsLowStockAlertOpen(false)}>
              OK, Got it
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivation confirmation dialog */}
      <AlertDialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Product?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to deactivate <strong>{productName}</strong>? The product will no
              longer be visible to buyers, but you can reactivate it later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-3 pt-4">
            <AlertDialogCancel onClick={handleCancelDeactivation}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDeactivation} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deactivating...
                </>
              ) : (
                'Deactivate'
              )}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export type { ProductStatusToggleProps };
